import { useEffect, useState } from 'react';
import Badge from '../../../components/badge/Badge';
import Card from '../../../components/card/Card';
import Table from '../../../components/table/table';
import { useClientTheme } from '../../../context/ThemeContext';
import P from '../../../components/texts/p/P';
import { riskEntities } from '../../../mocks/RiskEntities';
import { MONTH_NAMES, RISK_CODES, RISK_STATUS } from '../../../utils/Constants';
import {
  balanceInfo,
  indicator,
  riskData,
} from '../../../types/interfaces.tsx';
import Span from '../../../components/texts/span/Span';
import Tooltip from '../../../components/tooltip/Tooltip';
import Spinner from '../../../components/Spinner/spinner';
import Image from '../../../components/image/Image';
import Icon from '../../../components/icon/Icon';
import Button from '../../../components/button/Button';
import { useToast } from '../../../components/toast/toast';
import Collapsable from '../../../components/collapsable/Collapsable';
import FilePreview from '../../common/FilePreview';
import { formatCurrency } from '../../../utils/Masks';
import { toNumber } from '../../../utils/Utils';
import H from '../../../components/texts/h/H';
import Divider from '../../../components/divider/Divider';
import { standardizeBalanceData } from '../../../utils/BalanceDataUtils';
import { StandardizedBalanceData } from '../../../types/interfaces.tsx';
import {
  RiskDataEntry,
  RiskDataMap,
  PostBalanceRow,
  FallOfDebtRow,
  SgrRow,
  SgrInstrumentRow,
  CheckLightResumeProps,
  BankDetail,
  SgrItem,
  SgrInstrumentDetail,
  SgrByInstrumentEntry,
} from '../../../types/risk.types';
import DualBalanceIndicators from './DualBalanceIndicators';
import BalanceDetailTable from '../../common/BalanceDetailTable';
import BalanceSummaryView from '../../common/BalanceSummaryView';
import { pickMostRecentLastExecution } from './lastExecutionDisplay';
import theme from '../../../styles/theme';
import styled from 'styled-components';
import { useConfig } from '../../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../../utils/quotationAssets';
import { currencyNumberFormatter } from '../../../utils/currencyLabel';
import {
  buildEmployerContributionsRows,
  type EmployerContributionsTableRow,
} from '../../../utils/riskEmployerContributionsRows';
import { useTenantUiSettings } from '../../../hooks/useTenantUiSettings';
import {
  DEFAULT_ATLANTIC_GROUP_ORDER,
  DEFAULT_ATLANTIC_GROUP_TITLES,
} from './atlanticBpbGroupMap';
import type { RiskIndicatorsUiSettings } from '../../../services/TenantUiService';
import NosisScoresSection from './NosisScoresSection';
import NosisRejectedChecksSection from './NosisRejectedChecksSection';
import {
  filterNosisDetailIndicators,
  resolveNosisTopScoreCodes,
} from './nosisTopScores';
import { isNosisRejectedChecksSectionVisible } from './nosisRejectedChecksFormat';
/** Figma: botones dual balance = borde, texto e ícono rojos; fill del ícono blanco. Wrapper div para que la clase esté en el DOM (Card no reenvía className). */
const DualBalanceDownloadButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  & button [data-testid^='icon-'],
  & button > i,
  & button i {
    background-color: #fff !important;
    background: #fff !important;
  }
  & button svg path {
    fill: #ffffff !important;
    stroke: #e74c3c !important;
  }
`;

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Función para verificar si los datos ya están en formato estandarizado
const isStandardizedBalanceData = (
  data: balanceInfo | StandardizedBalanceData | undefined | null
): data is StandardizedBalanceData => {
  return !!(
    data &&
    typeof data === 'object' &&
    'values' in data &&
    'totals' in data
  );
};

const CheckLightResume: React.FC<CheckLightResumeProps> = ({
  data,
  showBalanceInfo,
  isLoading,
  handleRiskData,
  handleRiskDataAndDebts,
  riskCodes,
  debts,
  documents,
  cuit,
  applicationId,
  hideInfo = true,
  hideArBureauSections = false,
  balanceData,
  postBalanceData,
  isPdfMode = false,
  onDownloadPdf,
  isPdfLoading = false,
  // Dual balance props (Cementos feature)
  showDualBalance = false,
  previousBalanceData,
  previousBpbIndicators,
  analystNotes,
  hideNotes = false,
}) => {
  const { clientTheme } = useClientTheme();
  const config = useConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(config);
  const {
    indicators: indicatorsUiSettings,
    indicatorOrder,
    isAtlanticLayout,
    summary: summaryUiSettings,
  } = useTenantUiSettings(showBalanceInfo);
  const isAtlanticLayoutEffective = isAtlanticLayout && showBalanceInfo;
  const nosisTopScoreCodes = resolveNosisTopScoreCodes({
    isAtlanticLayout: isAtlanticLayoutEffective,
    configuredCodes: summaryUiSettings?.nosisScores?.codes,
  });
  const bpbUiSettings: RiskIndicatorsUiSettings | null =
    isAtlanticLayoutEffective
      ? {
          columns: 1,
          groupOrder: [...DEFAULT_ATLANTIC_GROUP_ORDER],
          groupTitles: { ...DEFAULT_ATLANTIC_GROUP_TITLES },
          ...indicatorsUiSettings,
        }
      : null;
  const isCasfogEnabled = config.isCasfogEnabled ?? true;
  const getIndicatorDescription = (row: indicator) =>
    row.code === 'BPB_019' ? 'ROE' : row.description;
  const employerContributionRows = buildEmployerContributionsRows(
    debts,
    config.isRiskEmployerContributionsDetailEnabled ?? false
  );

  const currentBalanceCode =
    balanceData && typeof balanceData === 'object' && 'code' in balanceData
      ? (balanceData as any).code
      : undefined;
  const previousBalanceCode =
    previousBalanceData &&
    typeof previousBalanceData === 'object' &&
    'code' in previousBalanceData
      ? (previousBalanceData as any).code
      : undefined;

  const windowWidth = useWindowSize();
  const [displayFlex, setDisplayFlex] = useState(
    windowWidth > 1850 ? 'flex' : 'block'
  );
  const [riskDataMap, setRiskDataMap] = useState<RiskDataMap | undefined>(
    undefined
  );
  const [lastExecution, setLastExecution] = useState('');
  const [expandedSgrRows, setExpandedSgrRows] = useState<Set<string>>(
    new Set()
  );
  const [showFullBalance, setShowFullBalance] = useState(isPdfMode);

  useEffect(() => {
    setDisplayFlex(windowWidth > 1850 ? 'flex' : 'block');
  }, [windowWidth]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const transformedData = transformData(data);
      setRiskDataMap(transformedData);
      setLastExecution(
        getLastExecutionFromData(
          Object.values(transformedData).filter(
            (item) => item.risk_code !== RISK_CODES.bpb
          )
        )
      );

      const itemsWithError = data.filter((item) => item.error);

      // Delay toast calls to ensure Toast component is mounted
      if (itemsWithError.length > 0) {
        setTimeout(() => {
          itemsWithError.forEach((item) => {
            useToast.error({
              title: `Error en ${item.risk_code}: ${item.error?.message}`,
              id: 'risk-toast',
              description: '',
              closeButton: true,
              autoClose: 5000,
              toastId: `risk-error-${item.risk_code}`,
            });
          });
        }, 300);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Log para ver en QA qué envía el back para Balance y ventas (BPB/risk); en local no hay datos para probar
  useEffect(() => {
    const bpbIndicators = riskDataMap?.[riskEntities.bpb.label]?.indicators;
    if (bpbIndicators?.length || previousBpbIndicators?.length) {
      // eslint-disable-next-line no-console
      console.log(
        '[Resumen riesgo] Balance y ventas - indicadores actuales (BPB/risk)',
        bpbIndicators
      );
      // eslint-disable-next-line no-console
      console.log(
        '[Resumen riesgo] Balance y ventas - indicadores período anterior (previousBpbIndicators)',
        previousBpbIndicators
      );
    }
  }, [riskDataMap, previousBpbIndicators]);

  const transformData = (rawData: riskData[] | any): RiskDataMap => {
    if (!Array.isArray(rawData)) return {};
    return rawData.reduce<RiskDataMap>((acc, item) => {
      acc[item.risk_code] = {
        error: item.error,
        risk_code: item.risk_code,
        last_execution: item.last_execution || item.execution_date,
        indicators: item.indicators || [],
        nosis_check_detail_enabled: item.nosis_check_detail_enabled,
        nosis_check_detail: item.nosis_check_detail,
      };
      return acc;
    }, {});
  };

  const getLastExecutionFromData = (entries: RiskDataEntry[]) => {
    const lastExecutions = entries
      .map((item) => item.last_execution)
      .filter(Boolean) as string[];

    return pickMostRecentLastExecution(lastExecutions);
  };

  const getBadgeStatus = (status: string) => {
    const mapStatus = new Map();
    mapStatus.set(RISK_STATUS.approved.label, 'success');
    mapStatus.set(RISK_STATUS.revision.label, 'warning');
    mapStatus.set(RISK_STATUS.medium_risk.label, 'warning');
    mapStatus.set(RISK_STATUS.high_risk.label, 'danger');
    return mapStatus.get(status) ? mapStatus.get(status) : 'success';
  };

  const getStatusText = (statusCode: string) => {
    const status = Object.values(RISK_STATUS).find(
      (item) => item.label === statusCode
    );
    return status ? status.text : 'Estado desconocido';
  };

  const getDebtStatusFromSituation = (situation?: string) => {
    if (!situation) return null;

    const numericSituation = Number(situation);
    if (Number.isNaN(numericSituation)) return null;

    if (numericSituation === 1)
      return { text: 'Seguro', variant: 'success' as const };
    if (numericSituation === 2 || numericSituation === 3) {
      return { text: 'Revisar', variant: 'warning' as const };
    }
    if (numericSituation > 3)
      return { text: 'Riesgo Alto', variant: 'danger' as const };

    return null;
  };

  const formatPostBalanceLabel = (row: PostBalanceRow) => {
    if (row.label) return row.label;
    if (row.month !== undefined && row.month !== null) {
      const monthIndex = Math.max(0, Number(row.month) - 1);
      const monthName = MONTH_NAMES[monthIndex] || '';
      const yearText = row.year ? ` ${row.year}` : '';
      return `${monthName}${yearText}`.trim();
    }
    return '';
  };

  const formatCurrencyString = (value: number | string) =>
    String(formatCurrency(Number(value || 0), true));

  const formatBpbIndicatorValue = (value: string | undefined): string => {
    if (value == null || value === '') return value ?? '';
    const s = String(value).trim();
    // Convertir las leyendas viejas a N/A
    if (['Sin ventas', 'Sin deudas', 'Sin patrimonio neto'].includes(s)) {
      return 'N/A';
    }
    // Si es N/A o empieza con "Sin " (para casos edge), devolver N/A
    if (/^Sin |^N\/A$/i.test(s)) {
      return 'N/A';
    }
    if (s.endsWith('%')) {
      const num = parseFloat(s) || (s.includes(',') ? toNumber(s) : NaN);
      return Number.isNaN(num) ? s : `${Math.round(num)}%`;
    }
    if (s.includes('$')) {
      const neg = /^-/.test(s);
      const numStr = s.replace(/^-\$?\s*|\$?\s*/g, '').replace(/\s/g, '');
      const num = numStr.includes(',') ? toNumber(numStr) : parseFloat(numStr);
      if (Number.isNaN(num)) return s;
      const formatted = Math.round(num).toLocaleString('es-AR', {
        maximumFractionDigits: 0,
      });
      return (neg ? '-$ ' : '$ ') + formatted;
    }
    const num = parseFloat(s) || (s.includes(',') ? toNumber(s) : NaN);
    if (Number.isNaN(num)) return s;
    return Math.round(num).toLocaleString('es-AR', {
      maximumFractionDigits: 0,
    });
  };

  const handleCopyDocument = async (document?: string) => {
    if (!document) return;

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      useToast.error({
        title: 'No se pudo copiar el documento',
        id: 'risk-toast',
        description: 'Funcionalidad no soportada en este navegador.',
        closeButton: true,
        autoClose: 3000,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(document);
      useToast.success({
        title: 'Documento copiado',
        id: 'risk-toast',
        description: document,
        closeButton: true,
        autoClose: 2000,
      });
    } catch (error) {
      useToast.error({
        title: 'No se pudo copiar el documento',
        id: 'risk-toast',
        description: 'Intentá nuevamente.',
        closeButton: true,
        autoClose: 3000,
      });
    }
  };

  const getPostBalanceColumns = (header: string) => {
    return [
      {
        name: header,
        selector: (row: PostBalanceRow) => (
          <Card>
            <P
              type={
                row.label === 'Total anualizado' || row.label === 'Promedio'
                  ? '3'
                  : '4'
              }
              variant={
                row.label === 'Promedio'
                  ? 'primary'
                  : row.label === 'Total anualizado'
                    ? 'neutral-1'
                    : 'neutral'
              }
              modifier='regular'
              weight={
                row.label === 'Total anualizado'
                  ? 'bold'
                  : row.label === 'Promedio'
                    ? 'bold'
                    : 'regular'
              }
            >
              {formatPostBalanceLabel(row)}
            </P>
          </Card>
        ),
        grow: 2,
      },
      {
        name: '',
        selector: (row: PostBalanceRow) => (
          <Card>
            <Span
              type={
                row.label === 'Total anualizado' || row.label === 'Promedio'
                  ? '3'
                  : '4'
              }
              variant={
                row.label === 'Promedio'
                  ? 'primary'
                  : row.label === 'Total anualizado'
                    ? 'neutral-1'
                    : 'neutral'
              }
              modifier='regular'
              weight={
                row.label === 'Total anualizado'
                  ? 'bold'
                  : row.label === 'Promedio'
                    ? 'bold'
                    : 'regular'
              }
            >
              $ {formatCurrency(row.amount ?? 0, true)}
            </Span>
          </Card>
        ),
        right: true,
      },
    ];
  };

  const getColumns = (
    firstHeader: string = '',
    secondHeader: string = '',
    thirdHeader: string = ''
  ) => {
    return [
      {
        name: firstHeader,
        selector: (row: indicator) => (
          <Card>
            <P type='3' variant='neutral'>
              {getIndicatorDescription(row)}
            </P>
          </Card>
        ),
        grow: 1.3,
      },
      {
        name: secondHeader,
        selector: (row: indicator) => (
          <Card>
            <Tooltip
              position='top'
              msg={row.tooltip_message ? row.tooltip_message : row.value}
            >
              <Span type='3' variant='neutral'>
                {row.value}
              </Span>
            </Tooltip>
          </Card>
        ),
      },
      {
        name: thirdHeader,
        selector: (row: indicator) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.status ? (
              <Badge variant={getBadgeStatus(row.status)} type='text' size='sm'>
                {getStatusText(row.status)}
              </Badge>
            ) : (
              ''
            )}
          </div>
        ),
        grow: 1.2,
      },
    ];
  };

  const getShareholdersColumns = () => {
    return [
      {
        name: 'Nombre',
        selector: (row: indicator) => (
          <Card>
            <P type='3' variant='neutral'>
              {row.description}
            </P>
          </Card>
        ),
        grow: displayFlex === 'flex' ? 1.8 : 5,
      },
      {
        name: 'Cuit',
        selector: (row: indicator) => (
          <Card display='flex' alignItems='center' gap='8px'>
            <Span type='3' variant='neutral'>
              {row.document || '-'}
            </Span>
            {row.document ? (
              <Tooltip position='top' msg='Copiar documento'>
                <Icon
                  name='copy_outline'
                  variant='neutral'
                  modifier='regular'
                  width={18}
                  height={18}
                  cursor='pointer'
                  onClick={() => handleCopyDocument(row.document)}
                />
              </Tooltip>
            ) : null}
          </Card>
        ),
        grow: 2,
      },
      {
        name: '%',
        selector: (row: indicator) => {
          // Mostrar el porcentaje de participación si está disponible
          const participation = row.ownership_interest;

          // Convertir a número si es string o validar que sea un número
          const participationNumber =
            typeof participation === 'string'
              ? parseFloat(participation)
              : participation;

          const displayValue =
            participationNumber !== undefined &&
            participationNumber !== null &&
            !isNaN(participationNumber)
              ? `${participationNumber.toFixed(2)}%`
              : '-';

          return (
            <Card>
              <Span type='3' variant='neutral'>
                {displayValue}
              </Span>
            </Card>
          );
        },
        grow: 1,
      },
      {
        name: 'Score Nosis',
        selector: (row: indicator) => (
          <Card>
            <Tooltip position='top' msg={row.value || '-'}>
              <Span type='3' variant='neutral'>
                {row.value}
              </Span>
            </Tooltip>
          </Card>
        ),
        grow: 1.4,
      },
      {
        name: 'Bienes',
        selector: (row: indicator) => {
          const assetsValue = row.assets || '-';
          return (
            <Card>
              <Tooltip position='top' msg={assetsValue}>
                <Span type='3' variant='neutral'>
                  {assetsValue}
                </Span>
              </Tooltip>
            </Card>
          );
        },
        grow: 2,
      },
      {
        name: 'Valor',
        selector: (row: indicator) => {
          // Usar total_value si está disponible, sino amount
          const value = row.total_value ?? row.amount ?? 0;

          const formattedValue =
            typeof value === 'number'
              ? currencyNumberFormatter(localCurrencyCode).format(value)
              : value || '-';

          return (
            <Card>
              <Tooltip position='top' msg={formattedValue}>
                <Span type='3' variant='neutral'>
                  {formattedValue}
                </Span>
              </Tooltip>
            </Card>
          );
        },
        grow: 1.6,
      },
    ];
  };

  const getBpbData = (): indicator[] => {
    const bpbIndicators = riskDataMap?.[riskEntities.bpb.label]?.indicators;
    if (!bpbIndicators) return [];

    const bpbDataOrdered = [...bpbIndicators].sort(
      (a: indicator, b: indicator) => {
        const indicatorA = parseInt(a.code.match(/BPB_(\d+)$/)?.[1] || '0', 10);
        const indicatorB = parseInt(b.code.match(/BPB_(\d+)$/)?.[1] || '0', 10);
        return indicatorA - indicatorB;
      }
    );

    return bpbDataOrdered;
  };

  const getNosisData = (): indicator[] => {
    const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators;
    if (!nosisIndicators) return [];

    const nosisData = [...nosisIndicators].sort(
      (a: indicator, b: indicator) => {
        const indicatorA = parseInt(
          a.code.match(/NOSIS_(\d+)$/)?.[1] || '0',
          10
        );
        const indicatorB = parseInt(
          b.code.match(/NOSIS_(\d+)$/)?.[1] || '0',
          10
        );
        return indicatorA - indicatorB;
      }
    );

    const filtered = nosisData.filter((item: indicator) => {
      const match = item.code.match(/NOSIS_(\d+)$/);
      return match && match[1].length <= 3;
    });

    return filtered;
  };

  const getShareholdersData = (): indicator[] => {
    const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators;
    if (!nosisIndicators) return [];

    const shareholderIndicators = nosisIndicators.filter((item: indicator) => {
      const match = item.code.match(/NOSIS_(\d+)$/);
      return match && match[1].length > 3;
    });

    return shareholderIndicators.map((item: indicator) => {
      // Usar directamente los campos del objeto
      // Los datos ahora vienen directamente desde el backend
      return {
        ...item,
        document: item.document || '', // Ya viene del backend
        assets: item.assets || '', // Ya viene del backend
        amount: item.total_value ?? item.amount ?? 0, // Priorizar total_value
      };
    });
  };

  const renderErrorText = () => {
    return (
      <Card margin={{ top: '1' }}>
        <P type='2' variant='warning' withMargin={false}>
          No se pudieron obtener datos, intentá nuevamente.
        </P>
        <P type='2' variant='warning' withMargin={false}>
          Se muestran los últimos datos obtenidos.
        </P>
      </Card>
    );
  };

  const getFallOfTheDebtColumns = () => {
    return [
      {
        name: 'Banco',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <P
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.name}
            </P>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'A 12 meses',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.twelve_months_amount ? row.twelve_months_amount : '-'}
            </Span>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'A 6 meses',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.six_months_amount ? row.six_months_amount : '-'}
            </Span>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'Actual',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Tooltip
              position='top'
              msg={row.current_amount ? row.current_amount : '-'}
            >
              <Span
                type='3'
                variant='neutral'
                weight={row.isTotal ? 'bold' : 'regular'}
              >
                {row.current_amount ? row.current_amount : '-'}
              </Span>
            </Tooltip>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Situación',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.situation ? row.situation : '-'}
            </Span>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Días de atraso',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.daysLate ? row.daysLate : '-'}
            </Span>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Estado',
        selector: (row: FallOfDebtRow) => {
          const statusInfo = getDebtStatusFromSituation(row.situation);
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {statusInfo ? (
                <Badge variant={statusInfo.variant} type='text' size='sm'>
                  {statusInfo.text}
                </Badge>
              ) : (
                <Span
                  type='3'
                  variant='neutral'
                  weight={row.isTotal ? 'bold' : 'regular'}
                >
                  -
                </Span>
              )}
            </div>
          );
        },
        grow: 1,
      },
    ];
  };

  const getEmployerContributionsColumns = () => [
    {
      name: 'Período',
      selector: (row: EmployerContributionsTableRow) => (
        <Card>
          <Span type='3' variant='neutral'>
            {row.period || '-'}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Aportes de seguro social',
      center: true,
      selector: (row: EmployerContributionsTableRow) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Badge
            variant={getEmployerContributionBadgeStatus(
              row.socialSecurityStatus
            )}
            type='text'
            size='sm'
          >
            {row.socialSecurityStatus || 'Sin datos'}
          </Badge>
        </div>
      ),
      grow: 1,
    },
    {
      name: 'Aportes de obra social',
      center: true,
      selector: (row: EmployerContributionsTableRow) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Badge
            variant={getEmployerContributionBadgeStatus(
              row.healthInsuranceStatus
            )}
            type='text'
            size='sm'
          >
            {row.healthInsuranceStatus || 'Sin datos'}
          </Badge>
        </div>
      ),
      grow: 1,
    },
    {
      name: 'Contrib. patronal obra social',
      center: true,
      selector: (row: EmployerContributionsTableRow) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Badge
            variant={getEmployerContributionBadgeStatus(
              row.employerHealthContributionStatus
            )}
            type='text'
            size='sm'
          >
            {row.employerHealthContributionStatus || 'Sin datos'}
          </Badge>
        </div>
      ),
      grow: 1,
    },
  ];

  const getEmployerContributionBadgeStatus = (
    status: string
  ): 'success' | 'warning' | 'danger' | 'secondary' => {
    const normalized = status?.trim().toUpperCase();
    if (normalized === 'PAGO') return 'success';
    if (normalized === 'PAGO PARCIAL') return 'warning';
    if (normalized === 'IMPAGO') return 'danger';
    return 'secondary';
  };

  const getFallOfTheDebtTableData = (): FallOfDebtRow[] => {
    const bankDetails =
      debts?.banks?.detail?.map(
        (bank: BankDetail): FallOfDebtRow => ({
          ...bank,
          daysLate: bank.days_past_due,
        })
      ) || [];

    const totalRow: FallOfDebtRow = {
      name: 'Total',
      twelve_months_amount: debts?.banks?.twelve_months_amount_total,
      six_months_amount: debts?.banks?.six_months_amount_total,
      current_amount: debts?.banks?.current_amount_total,
      situation: debts?.banks?.situation_total,
      daysLate: debts?.banks?.days_late_total,
      status: debts?.banks?.status_total,
      isTotal: true,
    };
    return [...bankDetails, totalRow];
  };

  const buildSgrTableData = (): SgrRow[] => {
    const rows =
      debts?.sgrs?.map(
        (sgr: SgrItem): SgrRow => ({
          name: sgr.name,
          daysLate: sgr.days_past_due,
          balance: sgr.current_amount,
        })
      ) || [];

    if (rows.length > 0) {
      rows.push({
        name: 'Total',
        daysLate: '',
        balance: String(
          formatCurrency(
            (debts?.sgrs || []).reduce((acc: number, sgr: SgrItem) => {
              // remover "$", espacios y luego parsear
              const cleanedValue = (sgr.current_amount || '0')
                .toString()
                .replace(/\$/g, '')
                .replace(/\s/g, '')
                .trim();
              return acc + toNumber(cleanedValue);
            }, 0),
            true
          )
        ),
        isTotal: true,
      });
    }

    return rows;
  };

  const getSgrColumns = () => [
    {
      name: 'SGR',
      selector: (row: SgrRow) => (
        <Tooltip position='top' msg={row.name}>
          <P
            type='3'
            variant='neutral'
            weight={row.isTotal ? 'bold' : 'regular'}
          >
            {row.name}
          </P>
        </Tooltip>
      ),
      grow: 1,
    },
    {
      name: 'Días de Atraso',
      selector: (row: SgrRow) => (
        <Span
          type='3'
          variant='neutral'
          weight={row.isTotal ? 'bold' : 'regular'}
        >
          {row.daysLate}
        </Span>
      ),
      grow: 1,
    },
    {
      name: 'Saldo',
      selector: (row: SgrRow) => (
        <Tooltip position='top' msg={row.balance}>
          <Span
            type='3'
            variant='neutral'
            weight={row.isTotal ? 'bold' : 'regular'}
          >
            {row.balance}
          </Span>
        </Tooltip>
      ),
      grow: 1,
    },
  ];

  const toggleSgrRow = (sgrName: string) => {
    setExpandedSgrRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sgrName)) {
        newSet.delete(sgrName);
      } else {
        newSet.add(sgrName);
      }
      return newSet;
    });
  };

  const calculateInstrumentTotal = (
    instruments: SgrInstrumentDetail[],
    instrumentName: string
  ) => {
    const instrument = instruments.find((i) => i.name === instrumentName);
    if (!instrument) return 0;
    return (
      (instrument.lessThanTwelveAmount || 0) +
      (instrument.betweenTwelveFortyEightAmount || 0) +
      (instrument.moreThanFortyEightAmount || 0)
    );
  };

  const buildSgrByInstrumentTableData = (): SgrInstrumentRow[] => {
    const sgrs = debts?.sgr_by_instrument?.sgrs || [];
    const total = debts?.sgr_by_instrument?.total;

    const rows: SgrInstrumentRow[] = [];

    // Add rows for each SGR
    sgrs.forEach((sgr: SgrByInstrumentEntry) => {
      const isExpanded = isPdfMode || expandedSgrRows.has(sgr.name);

      // Main row with totals
      rows.push({
        type: 'main',
        sgrName: sgr.name,
        bancario: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Bancario')
        ),
        chequeTercero: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Cheque de Tercero')
        ),
        chequePropio: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Cheque Propio')
        ),
        pagare: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Pagaré')
        ),
        on: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'On')
        ),
        otros: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Otros')
        ),
        total: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Total')
        ),
        isExpanded,
      });

      // Add detail rows if expanded
      if (isExpanded) {
        const periods = [
          { label: '< 12 meses', field: 'lessThanTwelveAmount' },
          { label: 'E. 12 y 48', field: 'betweenTwelveFortyEightAmount' },
          { label: '> 48 meses', field: 'moreThanFortyEightAmount' },
        ] as const;

        periods.forEach((period) => {
          const bancarioInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Bancario'
          );
          const chequeTerceroInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Cheque de Tercero'
          );
          const chequePropioInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Cheque Propio'
          );
          const pagareInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Pagaré'
          );
          const onInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'On'
          );
          const otrosInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Otros'
          );

          const bancarioVal = bancarioInst?.[period.field] || 0;
          const chequeTerceroVal = chequeTerceroInst?.[period.field] || 0;
          const chequePropioVal = chequePropioInst?.[period.field] || 0;
          const pagareVal = pagareInst?.[period.field] || 0;
          const onVal = onInst?.[period.field] || 0;
          const otrosVal = otrosInst?.[period.field] || 0;
          const totalVal =
            bancarioVal +
            chequeTerceroVal +
            chequePropioVal +
            pagareVal +
            onVal +
            otrosVal;

          rows.push({
            type: 'detail',
            sgrName: period.label,
            bancario: formatCurrencyString(bancarioVal),
            chequeTercero: formatCurrencyString(chequeTerceroVal),
            chequePropio: formatCurrencyString(chequePropioVal),
            pagare: formatCurrencyString(pagareVal),
            on: formatCurrencyString(onVal),
            otros: formatCurrencyString(otrosVal),
            total: formatCurrencyString(totalVal),
          });
        });
      }
    });

    // Add total row
    if (total) {
      rows.push({
        type: 'total',
        sgrName: 'Total',
        bancario: formatCurrencyString(total.bankingTotalAmount || 0),
        chequeTercero: formatCurrencyString(
          total.checksTerceroTotalAmount || 0
        ),
        chequePropio: formatCurrencyString(total.checksPropioTotalAmount || 0),
        pagare: formatCurrencyString(total.promissoryNotesTotalAmount || 0),
        on: formatCurrencyString(total.onTotalAmount || 0),
        otros: formatCurrencyString(total.othersTotalAmount || 0),
        total: formatCurrencyString(total.totalAmount || 0),
      });
    }

    return rows;
  };

  const getSgrByInstrumentColumns = () => [
    {
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <P
            type='3'
            variant='neutral-1'
            weight={row.type === 'detail' ? 'regular' : 'bold'}
          >
            {row.sgrName}
          </P>
        </Card>
      ),
      grow: 2,
    },
    {
      name: 'Bancario',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.bancario}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Cheque Tercero',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.chequeTercero}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Cheque Propio',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.chequePropio}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Pagaré',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.pagare}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'On',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.on}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Otros',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.otros}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Total',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant={row.type === 'total' ? 'success' : 'neutral-1'}
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.total}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: '',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          {row.type === 'main' && (
            <Icon
              name={
                row.isExpanded ? 'chevron_up_outline' : 'chevron_down_outline'
              }
              variant='neutral'
              modifier='strong'
              width={20}
              height={20}
              cursor='pointer'
              onClick={() => toggleSgrRow(row.sgrName)}
            />
          )}
        </Card>
      ),
      width: '2%',
      right: true,
    },
  ];

  const salesData = [
    ...(postBalanceData?.sales || []),
    {
      label: 'Total anualizado',
      amount: postBalanceData?.salesAnnualized || 0,
    },
    {
      label: 'Promedio',
      amount: postBalanceData?.salesAverage || 0,
    },
  ];

  const purchasesData = [
    ...(postBalanceData?.purchases || []),
    {
      label: 'Total anualizado',
      amount: postBalanceData?.purchasesAnnualized || 0,
    },
    {
      label: 'Promedio',
      amount: postBalanceData?.purchasesAverage || 0,
    },
  ];

  const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators
    ?.length
    ? getNosisData()
    : [];
  const shareholdersData = getShareholdersData();

  const getStatusBadge = (status?: string | null) =>
    status ? (
      <Badge variant={getBadgeStatus(status)} type='text' size='sm'>
        {getStatusText(status)}
      </Badge>
    ) : null;

  const hasNosisSection =
    (riskDataMap?.[riskEntities.nosis.label]?.indicators?.length ?? 0) > 0;

  const nosisScoresNode = hasNosisSection ? (
    <NosisScoresSection
      indicators={nosisIndicators}
      preferCodes={isAtlanticLayoutEffective}
      codes={nosisTopScoreCodes}
      renderStatusBadge={getStatusBadge}
      showSectionTitle={isAtlanticLayoutEffective}
    />
  ) : null;

  const isNewRejectedChecksSectionVisible = isNosisRejectedChecksSectionVisible(
    riskDataMap?.[riskEntities.nosis.label]?.nosis_check_detail_enabled,
    summaryUiSettings?.nosisRejectedChecks?.enabled
  );
  const hideLegacyRejectedChecksTable =
    isAtlanticLayoutEffective && isNewRejectedChecksSectionVisible;
  const nosisDetailTableData = filterNosisDetailIndicators(
    nosisIndicators,
    isAtlanticLayoutEffective
      ? { topScoreCodes: nosisTopScoreCodes }
      : undefined
  );

  return (
    <div
      data-testid={
        isAtlanticLayoutEffective
          ? 'atlantic-checklight-inner'
          : 'legacy-checklight-layout'
      }
      style={hideArBureauSections ? { display: 'flow-root' } : undefined}
    >
      <Card
        style={
          hideArBureauSections ? { maxWidth: '100%', minWidth: 0 } : undefined
        }
      >
        {/* Toast removido - usa el toast del componente padre (RiskResume) */}
        {isLoading && !riskDataMap ? (
          <Card
            display='flex'
            justifyContent='center'
            alignItems='center'
            height='200px'
          >
            <Spinner size='lg' variant='primary' />
          </Card>
        ) : riskDataMap ? (
          <Card>
            {isAtlanticLayoutEffective && nosisScoresNode}
            {showBalanceInfo &&
              riskDataMap?.[riskEntities.bpb.label]?.indicators && (
                <Card width='100%'>
                  <Card
                    variant={clientTheme ? 'primary' : 'success'}
                    modifier='weak'
                    spacing={'2'}
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <H
                      type='3'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                    >
                      Balance y ventas posteriores al último balance
                    </H>
                    {!isPdfMode && (
                      <Button
                        variant='primary'
                        size='md'
                        icon='download_outline'
                        iconAlign='left'
                        onClick={onDownloadPdf}
                        disabled={!onDownloadPdf || isPdfLoading}
                      >
                        {isPdfLoading ? (
                          <Spinner size='sm' variant='primary' />
                        ) : (
                          'Descargar Análisis PDF'
                        )}
                      </Button>
                    )}
                  </Card>
                  {/* BPB indicators: dual period (Cementos/Innova) vs single period (Movil) */}
                  {(() => {
                    const bpbData = getBpbData();
                    const currentPeriodLabel =
                      balanceData && 'to_month' in balanceData
                        ? `${MONTH_NAMES[(balanceData.to_month || 1) - 1]}-${balanceData.to_year}`
                        : 'Período Actual';

                    if (showDualBalance && previousBalanceData) {
                      const hasPreviousFromBackend =
                        previousBpbIndicators &&
                        previousBpbIndicators.length > 0;
                      const rawPrevious = hasPreviousFromBackend
                        ? previousBpbIndicators
                        : bpbData || [];
                      const previousIndicatorsToShow: indicator[] =
                        rawPrevious.map((ind) => ({
                          ...ind,
                          code:
                            ind.code ?? (ind as any).risk_indicator_code ?? '',
                        }));
                      const previousPeriodLabel =
                        previousBalanceData && 'to_month' in previousBalanceData
                          ? `${MONTH_NAMES[(previousBalanceData.to_month || 1) - 1]}-${previousBalanceData.to_year}`
                          : 'Período Anterior';
                      return (
                        <DualBalanceIndicators
                          indicators={bpbData}
                          previousIndicators={previousIndicatorsToShow}
                          previousPeriodLabel={previousPeriodLabel}
                          currentPeriodLabel={currentPeriodLabel}
                          comparePreviousPeriod
                          uiSettings={bpbUiSettings}
                          indicatorOrder={indicatorOrder}
                        />
                      );
                    }

                    return (
                      <DualBalanceIndicators
                        indicators={bpbData}
                        previousPeriodLabel=''
                        currentPeriodLabel={currentPeriodLabel}
                        comparePreviousPeriod={false}
                        uiSettings={bpbUiSettings}
                        indicatorOrder={indicatorOrder}
                      />
                    );
                  })()}
                </Card>
              )}

            {/* Atlantic: Nosis scores already rendered above balance */}
            {balanceData && (
              <Card margin={{ top: '6' }}>
                <Card
                  style={{
                    paddingBottom: '6px',
                    marginBottom: '8px',
                  }}
                >
                  {(() => {
                    const isDualBalanceView =
                      showDualBalance && previousBalanceData;
                    return (
                      <Collapsable
                        title='Ver balance y ventas posteriores al último balance'
                        titleVariant={
                          isDualBalanceView ? 'neutral-1' : 'primary'
                        }
                        titleModifier='strong'
                        iconVariant={isDualBalanceView ? 'neutral' : 'primary'}
                        startOpened={isPdfMode ? true : false}
                        forceOpen={isPdfMode}
                        hideToggle={isPdfMode}
                        headerStyle={(open) => {
                          const borderColor = isDualBalanceView
                            ? theme?.colors?.neutral?.text?.strong ?? '#231F20'
                            : theme?.colors?.['primary']?.border?.regular ??
                              '#03BAA1';
                          return open
                            ? {
                                borderBottom: `1px solid ${borderColor}`,
                                borderRadius: 0,
                                paddingBottom: '6px',
                              }
                            : {
                                border: `1px solid ${borderColor}`,
                                borderRadius: '6px',
                                padding: '6px 16px',
                              };
                        }}
                        children={
                          <Card margin={{ top: '3' }}>
                            {balanceData ? (
                              (() => {
                                const currStd = isStandardizedBalanceData(
                                  balanceData
                                )
                                  ? balanceData
                                  : standardizeBalanceData(balanceData);
                                const prevStd =
                                  showDualBalance && previousBalanceData
                                    ? isStandardizedBalanceData(
                                        previousBalanceData
                                      )
                                      ? previousBalanceData
                                      : standardizeBalanceData(
                                          previousBalanceData
                                        )
                                    : undefined;
                                const previousPeriodLabel =
                                  previousBalanceData &&
                                  'to_month' in previousBalanceData
                                    ? `${MONTH_NAMES[(previousBalanceData.to_month || 1) - 1]}-${previousBalanceData.to_year}`
                                    : 'Período anterior';
                                const currentPeriodLabel =
                                  balanceData && 'to_month' in balanceData
                                    ? `${MONTH_NAMES[(balanceData.to_month || 1) - 1]}-${balanceData.to_year}`
                                    : 'Período actual';

                                if (!showFullBalance) {
                                  return (
                                    <Card
                                      display='flex'
                                      flexDirection='column'
                                      gap='16px'
                                    >
                                      {showDualBalance &&
                                        prevStd &&
                                        !isPdfMode && (
                                          <DualBalanceDownloadButtons>
                                            <Button
                                              variant='secondary'
                                              size='md'
                                              icon='document_download_outline'
                                              iconAlign='left'
                                              onClick={onDownloadPdf}
                                              disabled={
                                                !onDownloadPdf || isPdfLoading
                                              }
                                              style={{
                                                boxShadow:
                                                  'inset 0 0 0 1px #e74c3c',
                                                color: '#e74c3c',
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                              }}
                                            >
                                              {previousPeriodLabel}
                                            </Button>
                                            <Button
                                              variant='secondary'
                                              size='md'
                                              icon='document_download_outline'
                                              iconAlign='left'
                                              onClick={onDownloadPdf}
                                              disabled={
                                                !onDownloadPdf || isPdfLoading
                                              }
                                              style={{
                                                boxShadow:
                                                  'inset 0 0 0 1px #e74c3c',
                                                color: '#e74c3c',
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                              }}
                                            >
                                              {currentPeriodLabel}
                                            </Button>
                                          </DualBalanceDownloadButtons>
                                        )}
                                      <BalanceSummaryView
                                        currentRiskData={currStd.values}
                                        currentTotals={currStd.totals}
                                        currentPeriodLabel={currentPeriodLabel}
                                        previousRiskData={prevStd?.values}
                                        previousTotals={prevStd?.totals}
                                        previousPeriodLabel={
                                          previousPeriodLabel
                                        }
                                        onViewFullDetail={() =>
                                          setShowFullBalance(true)
                                        }
                                        isPdfMode={isPdfMode}
                                      />
                                    </Card>
                                  );
                                }

                                const downloadButtonStyle = {
                                  boxShadow: 'inset 0 0 0 1px #e74c3c',
                                  color: '#e74c3c',
                                  backgroundColor: '#fff',
                                  borderRadius: '8px',
                                };

                                return (
                                  <Card
                                    display='flex'
                                    flexDirection='column'
                                    gap='16px'
                                  >
                                    <Card
                                      display='flex'
                                      justifyContent='space-between'
                                      alignItems='center'
                                    >
                                      <Button
                                        variant='secondary'
                                        size='sm'
                                        onClick={() =>
                                          setShowFullBalance(false)
                                        }
                                        style={{
                                          padding: '0 12px',
                                          gap: '6px',
                                        }}
                                      >
                                        <Icon
                                          name='arrow_left_outline'
                                          width={20}
                                          height={20}
                                        />
                                        Volver al resumen
                                      </Button>
                                      {!isPdfMode && (
                                        <DualBalanceDownloadButtons>
                                          {prevStd && (
                                            <Button
                                              variant='secondary'
                                              size='md'
                                              icon='document_download_outline'
                                              iconAlign='left'
                                              onClick={onDownloadPdf}
                                              disabled={
                                                !onDownloadPdf || isPdfLoading
                                              }
                                              style={downloadButtonStyle}
                                            >
                                              {previousPeriodLabel}
                                            </Button>
                                          )}
                                          <Button
                                            variant='secondary'
                                            size='md'
                                            icon='document_download_outline'
                                            iconAlign='left'
                                            onClick={onDownloadPdf}
                                            disabled={
                                              !onDownloadPdf || isPdfLoading
                                            }
                                            style={downloadButtonStyle}
                                          >
                                            {currentPeriodLabel}
                                          </Button>
                                        </DualBalanceDownloadButtons>
                                      )}
                                    </Card>
                                    <Card
                                      style={{
                                        backgroundColor:
                                          theme?.colors?.neutral?.bg?.regular ??
                                          '#F5F7FA',
                                        borderRadius: '8px',
                                        padding: '16px',
                                      }}
                                    >
                                      <BalanceDetailTable
                                        currentRiskData={currStd.values}
                                        currentTotals={currStd.totals}
                                        currentPeriodLabel={currentPeriodLabel}
                                        previousRiskData={prevStd?.values}
                                        previousTotals={prevStd?.totals}
                                        previousPeriodLabel={
                                          previousPeriodLabel
                                        }
                                        isDetailView
                                        isPdfMode={isPdfMode}
                                      />
                                    </Card>
                                  </Card>
                                );
                              })()
                            ) : (
                              <Card>
                                <P type='1' variant='warning' align='center'>
                                  No hay datos de balance disponibles.
                                </P>
                              </Card>
                            )}
                            <Card display={displayFlex} gap='16px'>
                              <Card
                                width={displayFlex === 'flex' ? '52%' : '100%'}
                              >
                                <Card width='100%'>
                                  <Table
                                    columns={getPostBalanceColumns('Ventas')}
                                    tableData={{
                                      size: salesData.length,
                                      totalElements: salesData.length,
                                    }}
                                    data={salesData}
                                    variant='weak'
                                    conditionalRowStyles={[
                                      {
                                        when: (row: PostBalanceRow) =>
                                          row.label === 'Total anualizado',
                                        style: {
                                          borderTop: '2px solid #4A4A4A',
                                        },
                                      },
                                    ]}
                                  />
                                </Card>
                              </Card>
                              <Card
                                width={displayFlex === 'flex' ? '52%' : '100%'}
                                margin={
                                  displayFlex === 'flex'
                                    ? { top: '0' }
                                    : { top: '5' }
                                }
                              >
                                <Card
                                  width={
                                    displayFlex === 'flex' ? '100%' : '100%'
                                  }
                                >
                                  <Table
                                    columns={getPostBalanceColumns('Compras')}
                                    tableData={{
                                      size: purchasesData.length,
                                      totalElements: purchasesData.length,
                                    }}
                                    data={purchasesData}
                                    variant='weak'
                                    conditionalRowStyles={[
                                      {
                                        when: (row: PostBalanceRow) =>
                                          row.label === 'Total anualizado',
                                        style: {
                                          borderTop: '2px solid #4A4A4A',
                                        },
                                      },
                                    ]}
                                  />
                                </Card>
                              </Card>
                            </Card>
                            {!isPdfMode &&
                              hideInfo &&
                              !hideArBureauSections && (
                                <Card margin={{ top: '5', bottom: '5' }}>
                                  <Card
                                    display='flex'
                                    justifyContent='space-between'
                                    gap='16px'
                                  >
                                    <FilePreview
                                      requiredDocumentId='010'
                                      file={documents?.find(
                                        (doc) => doc.type === '010'
                                      )}
                                      cuit={cuit || ''}
                                    />
                                    <FilePreview
                                      requiredDocumentId='009'
                                      file={documents?.find(
                                        (doc) => doc.type === '009'
                                      )}
                                      cuit={cuit || ''}
                                    />
                                  </Card>
                                </Card>
                              )}
                          </Card>
                        }
                      />
                    );
                  })()}
                </Card>
              </Card>
            )}
            {!hideArBureauSections &&
              riskDataMap?.[riskEntities.nosis.label]?.indicators?.length >
                0 && (
                <Card margin={{ top: '4' }} data-pdf-page-break='before'>
                  <Card
                    variant={clientTheme ? 'primary' : 'success'}
                    modifier='weak'
                    spacing={'2'}
                  >
                    <H
                      type='3'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                    >
                      Nosis
                    </H>
                  </Card>
                  <Card
                    margin={{ top: '4' }}
                    display='flex'
                    justifyContent='space-between'
                    gap='24px'
                    alignItems='center'
                  >
                    <P
                      type='1'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                    >
                      Chequeo de riesgo
                    </P>

                    <Card display='flex' gap='24px' alignItems='center'>
                      <P
                        type='2'
                        variant='neutral'
                        modifier='weak'
                        withMargin={false}
                      >
                        Última consulta: {lastExecution}
                      </P>
                      {!hideInfo && (
                        <Card width='210px'>
                          <Button
                            id='client-button-save'
                            size='lg'
                            variant='secondary'
                            fullWidth
                            onClick={() => handleRiskDataAndDebts?.()}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <Spinner size='sm' variant='primary' />
                            ) : (
                              'Realizar consulta'
                            )}
                          </Button>
                        </Card>
                      )}
                    </Card>
                  </Card>

                  {riskDataMap?.[riskEntities.nosis.label]?.error &&
                    renderErrorText()}
                  <Card margin={{ bottom: '5' }}>
                    {!isAtlanticLayoutEffective && nosisScoresNode}

                    {!hideLegacyRejectedChecksTable && hasNosisSection && (
                      <div data-testid='nosis-legacy-12m-table'>
                        <Card style={{ maxWidth: '600px' }}>
                          <Table
                            columns={[
                              {
                                name: 'Cheques rechazados (u. 12 meses)',
                                selector: (row: indicator) => (
                                  <Card>
                                    <P type='3' variant='neutral'>
                                      {getIndicatorDescription(row)}
                                    </P>
                                  </Card>
                                ),
                                grow: 2,
                              },
                              {
                                name: '',
                                selector: (row: indicator) => (
                                  <Card>
                                    <Span type='3' variant='neutral'>
                                      {row.value}
                                    </Span>
                                  </Card>
                                ),
                                right: true,
                              },
                            ]}
                            tableData={{ size: 10, totalElements: 10 }}
                            data={nosisDetailTableData}
                            variant='weak'
                          />
                        </Card>
                      </div>
                    )}

                    {hasNosisSection && (
                      <>
                        <Divider
                          variant='neutral'
                          modifier='regular'
                          height='1px'
                          margin={{ top: '3', bottom: '3' }}
                        />
                        <div data-testid='nosis-shareholders-section'>
                          <Card>
                            <P
                              type='1'
                              variant='neutral-1'
                              withMargin={false}
                              weight='bold'
                              modifier='strong'
                              style={{ marginBottom: 12 }}
                            >
                              Socios
                            </P>
                            <Table
                              columns={getShareholdersColumns()}
                              tableData={{ size: 10, totalElements: 10 }}
                              data={shareholdersData}
                              variant='weak'
                              noDataComponent={
                                <P
                                  type='3'
                                  variant='neutral'
                                  modifier='weak'
                                  withMargin={false}
                                  style={{
                                    textAlign: 'center',
                                    padding: '24px 0',
                                  }}
                                >
                                  No hay socios para mostrar
                                </P>
                              }
                            />
                          </Card>
                        </div>
                      </>
                    )}

                    {isNewRejectedChecksSectionVisible && (
                      <NosisRejectedChecksSection
                        detail={
                          riskDataMap?.[riskEntities.nosis.label]
                            ?.nosis_check_detail
                        }
                        isPdfMode={isPdfMode}
                      />
                    )}

                    {employerContributionRows.length > 0 && (
                      <Card margin={{ top: '4' }}>
                        <P
                          type='1'
                          variant='neutral-1'
                          withMargin={false}
                          weight='bold'
                          modifier='strong'
                          style={{ marginBottom: '12px' }}
                        >
                          Aportes patronales
                        </P>
                        <Table
                          data={employerContributionRows}
                          columns={getEmployerContributionsColumns()}
                          tableData={{
                            size: isPdfMode
                              ? employerContributionRows.length
                              : 30,
                            totalElements: employerContributionRows.length,
                          }}
                          variant='weak'
                          disablePagination={isPdfMode}
                        />
                      </Card>
                    )}
                  </Card>
                </Card>
              )}
            {hideInfo && !hideArBureauSections && (
              <Card margin={{ top: '4' }}>
                <Card display='flex' justifyContent='space-between' gap='16px'>
                  <FilePreview
                    requiredDocumentId='008'
                    file={documents?.find((doc) => doc.type === '008')}
                    cuit={cuit || ''}
                  />
                  <FilePreview
                    requiredDocumentId='007'
                    file={documents?.find((doc) => doc.type === '007')}
                    cuit={cuit || ''}
                  />
                </Card>
              </Card>
            )}
            {!hideArBureauSections && (
              <Card margin={{ top: '3', bottom: '4' }}>
                <Card
                  variant={clientTheme ? 'primary' : 'success'}
                  modifier='weak'
                  spacing={'2'}
                >
                  <H
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Centrales de deuda
                  </H>
                </Card>

                <Card display={displayFlex} margin={{ top: '5' }} gap='16px'>
                  {riskDataMap?.[riskEntities.bcra.label]?.indicators?.length >
                    0 && (
                    <Card
                      style={{ overflowX: 'hidden' }}
                      width={displayFlex === 'flex' ? '50%' : '100%'}
                    >
                      <P
                        type='2'
                        variant='neutral-1'
                        withMargin={false}
                        weight='bold'
                        modifier='strong'
                      >
                        Deuda BCRA
                      </P>
                      {riskDataMap?.[riskEntities.bcra.label]?.error &&
                        !riskDataMap?.[riskEntities.nosis.label]?.error &&
                        renderErrorText()}
                      <Table
                        columns={getColumns('Indicador', 'Valor', 'Estado')}
                        tableData={{ size: 10, totalElements: 10 }}
                        data={
                          riskDataMap?.[riskEntities.bcra.label]?.indicators ||
                          []
                        }
                        variant='weak'
                      />
                    </Card>
                  )}
                  {isCasfogEnabled &&
                    riskDataMap?.[riskEntities.casfog.label]?.indicators
                      ?.length > 0 && (
                      <Card
                        width={displayFlex === 'flex' ? '50%' : '100%'}
                        margin={
                          displayFlex === 'flex' ? { top: '0' } : { top: '5' }
                        }
                        style={{ overflowX: 'hidden' }}
                      >
                        <P
                          type='2'
                          variant='neutral-1'
                          withMargin={false}
                          weight='bold'
                          modifier='strong'
                        >
                          Deuda con otras S.G.R.
                        </P>
                        {riskDataMap?.[riskEntities.casfog.label]?.error &&
                          renderErrorText()}
                        <Table
                          columns={getColumns('Indicador', 'Valor', 'Estado')}
                          tableData={{ size: 10, totalElements: 10 }}
                          data={
                            riskDataMap?.[riskEntities.casfog.label]
                              ?.indicators || []
                          }
                          variant='weak'
                        />
                      </Card>
                    )}
                </Card>
              </Card>
            )}
          </Card>
        ) : hideArBureauSections ? null : (
          <Card>
            <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
              <P
                type='1'
                variant='neutral-1'
                withMargin={false}
                weight='bold'
                modifier='strong'
              >
                Chequeo de riesgo
              </P>
            </Card>
            <Card
              display='flex'
              justifyContent='center'
              gap='16px'
              margin={{ bottom: '4', top: '7' }}
            >
              <Image name='dashboard_empty' width={300} />
              <Card display='grid' gap='16px' width='500px'>
                <P
                  type='1'
                  withMargin={false}
                  variant='neutral'
                  modifier='regular'
                  weight='bold'
                >
                  No pudimos recuperar la información.
                </P>
                <P
                  type='1'
                  withMargin={false}
                  variant='neutral'
                  modifier='regular'
                >
                  La consulta falló porque no tenés conexión o bien el servicio
                  no está disponible. Volvé a intentarlo en unos minutos.
                </P>
              </Card>
            </Card>
            <Card width='200px' margin='auto'>
              <Button
                size='lg'
                onClick={() => handleRiskData(riskCodes)}
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Realizar Consulta'
                )}
              </Button>
            </Card>
          </Card>
        )}
        {hideArBureauSections || debts === undefined ? (
          ''
        ) : (
          <Card>
            {showBalanceInfo && (
              <Card margin={{ top: '4' }}>
                <Card
                  style={{
                    paddingBottom: '6px',
                    marginBottom: '8px',
                  }}
                >
                  <Collapsable
                    title='Ver detalle de deuda'
                    titleVariant='primary'
                    titleModifier='strong'
                    iconVariant='primary'
                    startOpened={true}
                    headerStyle={(open) =>
                      open
                        ? {
                            borderBottom: '1px solid #03BAA1',
                            borderRadius: 0,
                            paddingBottom: '6px',
                          }
                        : {
                            border: '1px solid #03BAA1',
                            borderRadius: '6px',
                            padding: '6px 16px',
                          }
                    }
                    forceOpen={isPdfMode}
                    hideToggle={isPdfMode}
                    children={
                      <Card margin={{ top: '4' }}>
                        <Card>
                          <P
                            type='2'
                            variant='neutral-1'
                            withMargin={false}
                            weight='bold'
                            modifier='strong'
                          >
                            Caída de la deuda
                          </P>

                          <Table
                            data={getFallOfTheDebtTableData()}
                            columns={getFallOfTheDebtColumns()}
                            tableData={{ size: 30, totalElements: 30 }}
                            variant='weak'
                          />
                        </Card>
                        {isCasfogEnabled && (
                          <Card>
                            <Card
                              width={displayFlex === 'flex' ? '52%' : '100%'}
                              margin={
                                displayFlex === 'flex'
                                  ? { top: '3' }
                                  : { top: '5' }
                              }
                            >
                              <P
                                type='2'
                                variant='neutral-1'
                                withMargin={false}
                                weight='bold'
                                modifier='strong'
                              >
                                Deuda con otras S.G.R.
                              </P>
                              <Card
                                width={displayFlex === 'flex' ? '100%' : '100%'}
                              >
                                <Table
                                  data={buildSgrTableData()}
                                  columns={getSgrColumns()}
                                  tableData={{ size: 30, totalElements: 30 }}
                                  variant='weak'
                                />
                              </Card>
                            </Card>
                            <Card margin={{ top: '4' }}>
                              <P
                                type='2'
                                variant='neutral-1'
                                withMargin={false}
                                weight='bold'
                                modifier='strong'
                              >
                                Caída de la deuda con SGR
                              </P>
                              {(() => {
                                const sgrByInstrumentData =
                                  buildSgrByInstrumentTableData();
                                return (
                                  <Table
                                    data={sgrByInstrumentData}
                                    columns={getSgrByInstrumentColumns()}
                                    tableData={{
                                      size: isPdfMode
                                        ? sgrByInstrumentData.length
                                        : 30,
                                      totalElements: isPdfMode
                                        ? sgrByInstrumentData.length
                                        : 30,
                                    }}
                                    variant='weak'
                                    disablePagination={isPdfMode}
                                  />
                                );
                              })()}
                            </Card>
                          </Card>
                        )}
                        {hideInfo && (
                          <Card margin={{ top: '4' }}>
                            <Card
                              display='flex'
                              justifyContent='space-between'
                              gap='16px'
                            >
                              <FilePreview
                                requiredDocumentId='003'
                                file={documents?.find(
                                  (doc) => doc.type === '003'
                                )}
                                cuit={cuit || ''}
                              />
                              <FilePreview
                                requiredDocumentId='005'
                                file={documents?.find(
                                  (doc) => doc.type === '005'
                                )}
                                cuit={cuit || ''}
                              />
                            </Card>
                          </Card>
                        )}
                      </Card>
                    }
                  />
                </Card>
              </Card>
            )}
            {hideInfo && !hideNotes && (
              <Card margin={{ top: '4' }}>
                <Card
                  variant={clientTheme ? 'primary' : 'success'}
                  modifier='weak'
                  spacing={'2'}
                >
                  <H
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Notas
                  </H>
                </Card>
                <Card margin={{ top: '3' }}>
                  <P type='2' variant='neutral' withMargin={false}>
                    {analystNotes && analystNotes.trim() !== ''
                      ? analystNotes
                      : 'sin comentarios'}
                  </P>
                </Card>
              </Card>
            )}
          </Card>
        )}
      </Card>
    </div>
  );
};

export default CheckLightResume;
